import {
  AttachmentDataFragment,
  useUploadAttachmentMutation
} from "@bonliva-crm/graphql";
import { useSnackbars, useUploadRequest } from "@hooks";
import locale from "@bonliva-crm/locale";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import { DeleteIcon, DraftIcon } from "@bonliva-crm/ui";
import filesize from "filesize";
import { FileUpload } from "mdi-material-ui";
import { FileUploadConfig } from "@bonliva-crm/shared";
import { DnDLocalContainer } from "@containers/DnDContainer";

type Props = {
  value: AttachmentDataFragment[];
  onChange: (value: AttachmentDataFragment[]) => void;
  label?: string;
};

const uploadConfig = new FileUploadConfig("email");

export const Attachments = (props: Props) => {
  const snack = useSnackbars();
  const { requestUpload } = useUploadRequest(false);

  const classes = useStyles();

  const [uploadAttachmentMutation, { loading }] = useUploadAttachmentMutation({
    onError: (e) => {
      console.error(e);
      snack.showError(locale.FileUploadFail);
    }
  });

  const uploadAttachment = (file: File) => {
    uploadAttachmentMutation({
      variables: {
        file: file
      },
      onCompleted: (data) => {
        snack.showSuccess(locale.FileUploadSucceeded);
        props.onChange([...props.value, data.uploadDocument.FileRef]);
      }
    });
  };

  return (
    <DnDLocalContainer
      onDrop={(files) => files.map((file) => uploadAttachment(file))}
      dropzoneOptions={{
        maxSize: uploadConfig.maxFileSize,
        accept: uploadConfig.acceptTypes
      }}
      overlayProps={{ className: classes.dndOverlay }}
      noClick
    >
      <Box className={classes.root}>
        <Typography variant="h5">{props.label || locale.Attachments}</Typography>
        {loading ? (
          <CircularProgress size={32} style={{ alignSelf: "center" }} />
        ) : (
          <>
            <Box className={classes.fileContainer}>
              {props.value.map(
                (file) =>
                  file && (
                    <Box key={file.NodeID} className={classes.file}>
                      <DraftIcon />
                      <Typography>{file.Name}</Typography>
                      {typeof file.Size === "number" && (
                        <Typography>{`(${filesize(file.Size, {
                          round: 0
                        })})`}</Typography>
                      )}
                      <IconButton
                        style={{ marginLeft: "auto" }}
                        onClick={() =>
                          props.onChange(
                            props.value.filter((val) => val.NodeID !== file.NodeID)
                          )
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )
              )}
            </Box>
            <Box className={classes.uploadContainer}>
              <Button
                startIcon={<FileUpload />}
                variant="contained"
                color="primary"
                onClick={() =>
                  requestUpload({
                    maxSize: uploadConfig.maxFileSize,
                    acceptMimeTypes: uploadConfig.acceptString
                  }).then((files) => files.map((file) => uploadAttachment(file)))
                }
                className={classes.uploadBtn}
              >
                {locale.UploadFile}
              </Button>
              <Typography variant="h5">{locale.DragAndDrop}</Typography>
              <Typography variant="h5">
                {locale.MaxFileSize(filesize(uploadConfig.maxFileSize))}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </DnDLocalContainer>
  );
};

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexDirection: "column", rowGap: "1rem" },
  fileContainer: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem",
    padding: "0 1rem"
  },
  file: {
    display: "flex",
    flexDirection: "row",
    columnGap: "0.5rem",
    alignItems: "center"
  },
  uploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  uploadBtn: { margin: "8px auto" },
  dndOverlay: {
    position: "absolute",
    width: "100%",
    height: "100%"
  }
}));
